import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    MapPin,
    Calendar,
    User,
    ChevronDown,
    Ticket,
    Heart,
    CheckCircle,
    Share2,
    ArrowLeft,
    ArrowRight,
    Play,
    X,
    ChevronRight,
    ChevronLeft,
    Store,
    Info,
    Clock,
    XCircle,
} from "lucide-react";

import { getEventDetails, getCountryList } from "../../stores/actions/eventsActions";
import { isProductionAppEnv } from "../../constant/constant";
import { getEventSubdomainSlug } from "../../utils/eventSubdomain";
import TicketSelectModal from "../../components/modal/TicketSelectModal";
import { setSelectedTicket } from "../../stores/slices/eventSlice";
import Payment from "../../components/modal/Payment";
import SocialShare from "../../components/modal/SocialShare";
import FlyerGallery from "../../components/modal/FlyerGallery";
import GoogleMap from "../../components/map/GoogleMapList";
import NotFound from "../../components/errors/NotFound";
import SkeltonLoader from "../../components/loader/SkeltonLoader";
import { request } from "../../utils/request";
import { buyerFeeForItem, feeConfigFromCountryRow } from "../../utils/fees";
import { Link } from "react-router-dom";
import { openModal, showError } from "../../stores/slices/commonSlice";
import { useHomeSearch } from "../../components/layouts/layouts";
import { FaInstagram, FaFacebookF, FaXTwitter, FaLinkedinIn } from "react-icons/fa6";
import { useHeroStickyTransition } from "../../hooks/useHeroStickyTransition";
// Follow-state cookie cache (`_aps_fwd_<host_id>`), shared with the
// /organizer/:slug profile page so both surfaces read and write the same key.
import { readFollowCookie, writeFollowCookie } from "../../utils/followCookies";

const ORGANIZER_SOCIAL_ICONS = [
    { key: "instagram", label: "Instagram", field: "instagram", Icon: FaInstagram, hoverColor: "#E4405F", baseUrl: "https://instagram.com/" },
    { key: "facebook", label: "Facebook", field: "facebook", Icon: FaFacebookF, hoverColor: "#1877F2", baseUrl: "https://facebook.com/" },
    { key: "x", label: "X", field: "twitter", altField: "x", Icon: FaXTwitter, hoverColor: "#111827", baseUrl: "https://x.com/" },
    { key: "linkedin", label: "LinkedIn", field: "linkedin", Icon: FaLinkedinIn, hoverColor: "#0A66C2", baseUrl: "https://linkedin.com/in/" },
];

// Build the full profile URL from the stored value. We persist just the
// username/handle (e.g. "apsession"), so we prepend the platform base URL.
// A value that's already a full URL (legacy data or a pasted link) is used
// as-is; a leading "@" on a handle is stripped.
const getOrganizerSocialHref = (organizer, field, altField, baseUrl) => {
    let value = organizer?.[field] || (altField ? organizer?.[altField] : null);
    if (typeof value !== "string") return null;
    value = value.trim().replace(/^@+/, "");
    if (value === "") return null;
    if (/^https?:\/\//i.test(value)) return value;
    return `${baseUrl}${value}`;
};


const EventDetailsSkeleton = () => (
    <div className="min-h-screen bg-gray-50 pb-28 md:pb-20 animate-pulse">
        {/* Hero cover + title row */}
        <div className="bg-white border-b border-gray-100">
            <div className="max-w-7xl mx-auto w-full sm:px-6 lg:px-8 sm:pt-6 lg:pt-8">
                <div className="w-full aspect-[2/1] bg-gray-200 sm:rounded-[2rem]" />
            </div>
            <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-5 md:py-8">
                <div className="flex items-center space-x-3 md:space-x-4 mb-4 md:mb-6">
                    <div className="h-7 md:h-8 w-24 bg-gray-200 rounded-lg" />
                    <div className="h-9 w-9 bg-gray-200 rounded-full" />
                    <div className="h-9 w-9 bg-gray-200 rounded-full" />
                </div>
                <div className="h-9 md:h-14 w-3/4 bg-gray-200 rounded-lg" />
            </div>
        </div>

        {/* Body grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                <div className="lg:col-span-2 space-y-12">
                    {/* When / Where card */}
                    <section className="bg-white rounded-[2rem] p-6 md:p-10 border border-gray-100 flex flex-col md:flex-row gap-6 lg:gap-12">
                        {[0, 1].map((i) => (
                            <div key={i} className="flex-1 space-y-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gray-200 rounded-xl" />
                                    <div className="h-4 w-20 bg-gray-200 rounded" />
                                </div>
                                <div className="h-6 w-48 bg-gray-200 rounded" />
                                <div className="h-4 w-36 bg-gray-200 rounded" />
                            </div>
                        ))}
                    </section>

                    {/* About */}
                    <section className="bg-white rounded-[2rem] p-6 md:p-10 border border-gray-100">
                        <div className="h-8 w-40 bg-gray-200 rounded mb-6" />
                        <div className="space-y-3">
                            <div className="h-4 w-full bg-gray-200 rounded" />
                            <div className="h-4 w-full bg-gray-200 rounded" />
                            <div className="h-4 w-5/6 bg-gray-200 rounded" />
                            <div className="h-4 w-2/3 bg-gray-200 rounded" />
                        </div>
                    </section>

                    {/* Headliners */}
                    <section className="bg-white rounded-[2rem] p-6 md:p-10 border border-gray-100">
                        <div className="h-8 w-40 bg-gray-200 rounded mb-6" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[0, 1].map((i) => (
                                <div key={i} className="flex items-center p-3 border border-gray-100 rounded-xl">
                                    <div className="w-14 h-14 bg-gray-200 rounded-full mr-3" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 w-32 bg-gray-200 rounded" />
                                        <div className="h-3 w-24 bg-gray-200 rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Tickets */}
                    <section className="bg-white rounded-[2rem] p-6 md:p-10 border border-gray-100">
                        <div className="h-8 w-32 bg-gray-200 rounded mb-6" />
                        <div className="space-y-3">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="p-4 border border-gray-200 rounded-xl flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
                                    <div className="space-y-2 flex-1">
                                        <div className="h-4 w-24 bg-gray-200 rounded-full" />
                                        <div className="h-5 w-40 bg-gray-200 rounded" />
                                    </div>
                                    <div className="flex w-full items-center justify-between gap-3 border-t border-gray-100 pt-4 sm:w-auto sm:border-0 sm:pt-0">
                                        <div className="h-5 w-24 bg-gray-200 rounded" />
                                        <div className="h-9 w-20 bg-gray-200 rounded-lg" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Location */}
                    <section className="bg-white rounded-[2rem] p-6 md:p-10 border border-gray-100">
                        <div className="flex justify-between items-center mb-6">
                            <div className="h-8 w-32 bg-gray-200 rounded" />
                            <div className="h-9 w-32 bg-gray-200 rounded-xl" />
                        </div>
                        <div className="h-5 w-3/4 bg-gray-200 rounded mb-6" />
                        <div className="w-full h-[250px] md:h-[350px] bg-gray-200 rounded-2xl" />
                    </section>

                    {/* Refund Policy */}
                    <section className="bg-gray-100 rounded-[2rem] p-6 md:p-10 border border-gray-200">
                        <div className="h-7 w-44 bg-gray-200 rounded mb-3" />
                        <div className="space-y-2">
                            <div className="h-4 w-full bg-gray-200 rounded" />
                            <div className="h-4 w-4/5 bg-gray-200 rounded" />
                        </div>
                    </section>

                    {/* Organizer */}
                    <section className="bg-white rounded-[2rem] p-6 md:p-8 border border-gray-100">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                            <div className="flex items-center space-x-4">
                                <div className="w-16 h-16 bg-gray-200 rounded-full" />
                                <div className="space-y-2">
                                    <div className="h-5 w-32 bg-gray-200 rounded" />
                                    <div className="h-3 w-24 bg-gray-200 rounded" />
                                </div>
                            </div>
                            <div className="h-11 w-32 bg-gray-200 rounded-full" />
                        </div>
                        <hr className="border-gray-100 my-6" />
                        <div className="space-y-2">
                            <div className="h-4 w-full bg-gray-200 rounded" />
                            <div className="h-4 w-5/6 bg-gray-200 rounded" />
                        </div>
                    </section>
                </div>

                {/* Desktop sidebar */}
                <div className="hidden lg:block lg:col-span-1">
                    <div className="sticky top-10 bg-white rounded-[2rem] border border-gray-100 overflow-hidden">
                        <div className="aspect-[2/1] bg-gray-200" />
                        <div className="p-8 space-y-5">
                            <div className="flex items-center justify-between">
                                <div className="h-4 w-20 bg-gray-200 rounded" />
                                <div className="h-8 w-32 bg-gray-200 rounded" />
                            </div>
                            <div className="h-14 w-full bg-gray-200 rounded-2xl" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export default function EventDetails() {
    const { slug: routeSlug } = useParams();
    // Resolve the event identifier. The normal `/events/:slug` route supplies
    // it as a URL param; the short URL (`<event_link>.apsession.com`) is served
    // in place at path "/" with no param, so we derive it from the hostname.
    // Either spelling works against the details endpoint, which matches on
    // `slug OR event_link` (Api\V1\EventController::details).
    // This component owns the fetch for both cases (DynamicHome renders it for
    // the subdomain) — keeping a single fetch so the page-visit counter is
    // incremented exactly once.
    const slug =
        routeSlug ||
        (isProductionAppEnv ? getEventSubdomainSlug(window.location.hostname) : null) ||
        undefined;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const subNavRef = useRef(null);
    const eventTitleRef = useRef(null);
    const homeSearch = useHomeSearch();
    const registerEventHeader = homeSearch?.registerEventHeader;
    const unregisterEventHeader = homeSearch?.unregisterEventHeader;
    const setShowMobileEventHeaderTitle = homeSearch?.setShowMobileEventHeaderTitle;

    const loading = useSelector((state) => state.common.loading);
    const eventDetails = useSelector((state) => state.event.eventDetails);
    const eventDetailsLoading = useSelector((state) => state.event.eventDetailsLoading);
    const country = useSelector((state) => state.event.country);
    const getCountryFeeListDetails = useSelector(
        (state) => state.event.getCountryFeeListDetails
    );

    // Existing 3-modal checkout chain — preserved from the old page so we
    // don't touch Stripe integration in this design pass.
    const [openTicketBooking, setOpenTicketBooking] = useState(false);
    const [openPayment, setOpenPayment] = useState(false);
    const [openSocialShare, setOpenSocialShare] = useState(false);

    // New-design local state for the layout itself.
    const [viewingGalleryIndex, setViewingGalleryIndex] = useState(null);
    const [showVideo, setShowVideo] = useState(false);
    const [isAboutExpanded, setIsAboutExpanded] = useState(false);
    const [activeSection, setActiveSection] = useState("about");
    // Related events are fetched separately from the event details payload
    // and live in local state so we can hide the entire Recommended block
    // when the API returns zero results.
    const [relatedEvents, setRelatedEvents] = useState([]);

    // Desktop FLIP morph: hero/sticky cards stay mounted as layout anchors,
    // while morphCardRef renders the one visible card that travels between them.
    const heroImageRef = useRef(null);
    const heroCardRef = useRef(null);
    const stickyCardRef = useRef(null);
    const morphCardRef = useRef(null);

    const { heroImageOut: desktopHeroOut, syncedReveal } = useHeroStickyTransition({
        heroImageRef,
        heroCardRef,
        stickyCardRef,
        morphCardRef,
        enabled: Boolean(eventDetails?.id) && !loading,
    });

    // Follow state for the host card. Mirrors organizer.{following,followers}
    // from the API on first load, then mutates locally on click (optimistic
    // UI). isFollowPending prevents double-tap races.
    const [isFollowing, setIsFollowing] = useState(false);
    const [followersCount, setFollowersCount] = useState(0);
    const [isFollowPending, setIsFollowPending] = useState(false);

    // Auth state — needed to gate the Follow Host action.
    const authUser = useSelector((state) => state.auth?.user);
    const isLoggedIn = !!(authUser && authUser.id);

    useEffect(() => {
        if (!getCountryFeeListDetails) {
            dispatch(getCountryList());
        }
    }, []);

    // Fetch the event once the slug is resolved (URL param or subdomain).
    // Guarded so we never call details() with an empty slug.
    useEffect(() => {
        if (!slug) return;
        dispatch(getEventDetails({ slug }));
    }, [slug]);

    useEffect(() => {
        if (!eventDetails?.name || !registerEventHeader) return undefined;
        registerEventHeader({ title: eventDetails.name });
        return () => unregisterEventHeader?.();
    }, [eventDetails?.name, registerEventHeader, unregisterEventHeader]);

    useEffect(() => {
        if (!setShowMobileEventHeaderTitle) return undefined;

        const mq = window.matchMedia("(min-width: 768px)");
        let observer = null;

        const attachObserver = (titleEl) => {
            if (observer) {
                observer.disconnect();
                observer = null;
            }
            if (!titleEl || mq.matches) {
                setShowMobileEventHeaderTitle(false);
                return;
            }
            observer = new IntersectionObserver(
                ([entry]) => {
                    setShowMobileEventHeaderTitle(!entry.isIntersecting);
                },
                { threshold: 0, rootMargin: "-80px 0px 0px 0px" }
            );
            observer.observe(titleEl);
        };

        const syncObserver = () => {
            attachObserver(eventTitleRef.current);
        };

        syncObserver();
        const refCheckId = window.requestAnimationFrame(syncObserver);
        mq.addEventListener("change", syncObserver);

        return () => {
            window.cancelAnimationFrame(refCheckId);
            mq.removeEventListener("change", syncObserver);
            if (observer) observer.disconnect();
            setShowMobileEventHeaderTitle(false);
        };
    }, [setShowMobileEventHeaderTitle, eventDetails?.name]);

    useEffect(() => {
        // The username slug, never the numeric user id — the API stopped
        // publishing primary keys. It's also what the follow endpoints take,
        // so the cookie key and the request path stay in agreement.
        const orgId = eventDetails?.organizer?.profile_path;
        if (eventDetails?.organizer) {
            const fromApi = !!eventDetails.organizer.following;
            const fromCookie = readFollowCookie(orgId);

            setIsFollowing(fromApi || fromCookie);
            setFollowersCount(Number(eventDetails.organizer.followers || 0));

            writeFollowCookie(orgId, fromApi);
        } else {
            setIsFollowing(false);
            setFollowersCount(0);
        }
    }, [eventDetails?.organizer?.profile_path]);

    useEffect(() => {
        if (!eventDetails?.id) return;
        let cancelled = false;
        request
            .get(`event/${eventDetails.id}/related`)
            .then((res) => {
                if (cancelled) return;
                const list = Array.isArray(res?.data?.result) ? res.data.result : [];
                setRelatedEvents(list);
            })
            .catch(() => {
                if (!cancelled) setRelatedEvents([]);
            });
        return () => { cancelled = true; };
    }, [eventDetails?.id]);

    // Sticky sub-nav active-section tracking. Listens to scroll and flags the
    // section whose top has crossed the (sticky-nav + sub-nav) offset.
    useEffect(() => {
        const handleScroll = () => {
            const sections = ["about", "headliners", "tickets", "flyers", "outlets"];
            const scrollPos = window.scrollY + 150;
            let current = "about";
            sections.forEach((id) => {
                const el = document.getElementById(id);
                if (el && el.offsetTop <= scrollPos) current = id;
            });
            setActiveSection(current);
        };
        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, [eventDetails]);

    // Auto-scroll the sub-nav so the active tab stays centered on mobile.
    useEffect(() => {
        if (subNavRef.current && activeSection) {
            const activeTab = subNavRef.current.querySelector(`a[href="#${activeSection}"]`);
            if (activeTab) {
                const container = subNavRef.current;
                const scrollLeft =
                    activeTab.offsetLeft -
                    container.offsetWidth / 2 +
                    activeTab.offsetWidth / 2;
                container.scrollTo({ left: scrollLeft, behavior: "smooth" });
            }
        }
    }, [activeSection]);

    /* -------- Checkout modal chain (unchanged from the old page) ----------- */
    // New checkout: the select modal hands the flat cart straight to Payment.
    // Attendee details are dropped — every ticket uses the buyer's own account
    // info (the backend order requires firstName/lastName/email/phone per line).
    const handleContinue = (selectedTicket) => {
        const u = authUser || {};
        const nameParts = String(u.username || '').trim().split(/\s+/).filter(Boolean);
        const firstName = nameParts[0] || (u.email ? String(u.email).split('@')[0] : 'Guest');
        const lastName = nameParts.slice(1).join(' ');
        const ticketUserDetails = selectedTicket.map(() => ({
            firstName,
            lastName,
            email: u.email || '',
            confirm_email: u.email || '',
            phone: u.phone || '',
        }));
        dispatch(setSelectedTicket({ selectedTicket, ticketUserDetails }));
        setOpenTicketBooking(false);
        setOpenPayment(true);
    };

    /* -------- Display helpers --------------------------------------------- */
    const formatDate = (dateString, options) => {
        if (!dateString) return "";
        const [y, m, d] = String(dateString).split("T")[0].split("-").map(Number);
        if (!y || !m || !d) return dateString;
        return new Date(y, m - 1, d).toLocaleDateString("en-US", options || {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const formatTimeRange = () => {
        const start = (eventDetails?.time || "").slice(0, 5);
        const end = (eventDetails?.end_time || "").slice(0, 5);
        if (!start) return "";
        return end ? `${start} - ${end}` : start;
    };

    // Format an ISO8601 ticket sale boundary (sale_starts / sale_ends) for
    // display on the ticket card, e.g. "Jun 20, 2026, 10:00 AM".
    const formatSaleDateTime = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        return d.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    };

    const amountFormatter = (amount) =>
        new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);

    // Compact follower count: 980 → "980", 1_500 → "1.5k", 1_500_000 → "1.5M".
    // Hand-rolled because Intl.NumberFormat({notation:'compact'}) outputs
    // "1.5K" with an uppercase K in en-US, which mismatches the design's
    // lowercase "15.4k". We follow the social-media convention: lowercase
    // `k` for thousands, uppercase `M` for millions.
    const formatCount = (n) => {
        const v = Math.max(0, Number(n) || 0);
        if (v < 1_000) return String(v);
        if (v < 1_000_000) {
            return (v / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
        }
        if (v < 1_000_000_000) {
            return (v / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
        }
        return (v / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
    };

    // Toggle follow state on the host card. Optimistic update first; if the
    // request fails we roll back. Guests trigger the login modal instead of
    // a 401 round-trip.
    const handleToggleFollow = async () => {
        const hostId = eventDetails?.organizer?.profile_path;
        if (!hostId || isFollowPending) return;

        if (!isLoggedIn) {
            dispatch(openModal()); // existing app-wide login modal
            return;
        }

        const previousFollowing = isFollowing;
        const previousCount = followersCount;
        const nextFollowing = !previousFollowing;

        // Optimistic UI flip + cookie write — feels instant on the next
        // page load too, since the cookie is read in the hydration effect.
        setIsFollowing(nextFollowing);
        setFollowersCount((c) => Math.max(0, c + (nextFollowing ? 1 : -1)));
        writeFollowCookie(hostId, nextFollowing);
        setIsFollowPending(true);

        try {
            const res = nextFollowing
                ? await request.post(`organizer/${hostId}/follow`)
                : await request.delete(`organizer/${hostId}/follow`);

            // Sync to whatever the server actually committed (avoids drift
            // in edge cases like the host getting deleted between calls)
            // and re-pin the cookie to match the server's view.
            if (res?.data) {
                if (typeof res.data.following === "boolean") {
                    setIsFollowing(res.data.following);
                    writeFollowCookie(hostId, res.data.following);
                }
                if (typeof res.data.followers_count === "number") {
                    setFollowersCount(res.data.followers_count);
                }
            }
        } catch (err) {
            // Roll back to the pre-click state — including the cookie,
            // so the next page load doesn't claim a follow that never
            // committed on the server.
            setIsFollowing(previousFollowing);
            setFollowersCount(previousCount);
            writeFollowCookie(hostId, previousFollowing);
            dispatch(showError(err?.response?.data?.message || "Could not update follow status."));
        } finally {
            setIsFollowPending(false);
        }
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: eventDetails?.name,
                    text: `Check out ${eventDetails?.name} on Apsession!`,
                    url: window.location.href,
                });
            } catch (err) {
                // user dismissed share sheet — fine
            }
        } else {
            setOpenSocialShare(true);
        }
    };

    // Gate on the DEDICATED details flag + the eventDetails value, NOT the
    // shared common.loading (a concurrent getCountryList used to flip that false
    // mid-fetch, dropping the page to a blank body on first load). eventDetails
    // is '' until the first payload, an object once loaded, and null only after a
    // failed/not-found load.
    if (eventDetails === null) {
        return <NotFound />;
    }

    // First page load (or a slug change that hasn't returned yet) — show the
    // full-page skeleton until the payload arrives. `|| !eventDetails` also
    // covers the very first paint before the fetch effect dispatches `pending`.
    if (eventDetailsLoading || !eventDetails) {
        return <EventDetailsSkeleton />;
    }

    /* -------- Derived values from the EventResource payload --------------- */
    const galleryImages = eventDetails
        ? [eventDetails.cover_image, eventDetails.flyer1, eventDetails.flyer2].filter(Boolean)
        : [];


    // Host-level kill switch for selling through Apsession (events.online_ticketing).
    // When it's 0 the tier list still renders — buyers can see what exists and
    // what it costs — but every path into checkout is gone: no per-tier Buy
    // button, no Get Tickets CTA, no mobile CTA bar, no checkout modal. Every
    // gate below keys off this one flag.
    const onlineTicketingEnabled = Boolean(eventDetails?.online_ticketing);

    // "Starts at" sidebar price: cheapest non-zero ticket price.
    const startingPrice = (() => {
        const buyable = (eventDetails?.tickets || []).filter(
            (t) => Number(t.price) > 0
                && !t.is_sold_out
                && (t.is_unlimited || Number(t.quantity_left ?? 0) > 0)
        );
        if (buyable.length === 0) return null;
        const min = Math.min(...buyable.map((t) => Number(t.price)));
        const cur = buyable[0].currency || "$";
        return `${cur} ${amountFormatter(min)}`;
    })();

    const hasBuyableTickets = onlineTicketingEnabled
        && (eventDetails?.tickets || []).some(
            (t) => !t.is_sold_out && (t.is_unlimited || Number(t.quantity_left) > 0)
        );

    // Prefer the event's server-resolved fee settings (honors custom_pricing_id)
    // so the per-ticket "+ Fee" hint matches the Select Tickets modal + checkout;
    // fall back to the buyer's selected-country row for non-custom-priced events.
    const ticketFeeConfig = feeConfigFromCountryRow(
        eventDetails?.fee_settings ||
        (getCountryFeeListDetails || []).find((d) => d.country === country)
    );

    const eventEnded = (() => {
        const datePart = eventDetails?.end_date || eventDetails?.date;
        if (!datePart) return false;
        const [y, m, d] = String(datePart).split("-").map(Number);
        if (!y || !m || !d) return false;
        const [hh, mm] = (eventDetails?.end_time || eventDetails?.time || "23:59")
            .slice(0, 5)
            .split(":")
            .map(Number);
        return new Date() > new Date(y, m - 1, d, hh || 0, mm || 0);
    })();


    const hasDescription = !!eventDetails?.description;
    const hasOtherInfo = !!eventDetails?.otherinfo;

    const heroImage =
        eventDetails?.horizontal_flyer ||
        eventDetails?.cover_image ||
        "/assets/images/event-cover-paceholder.png";

    const videoUrl =
        typeof eventDetails?.videoUrl === "string" ? eventDetails.videoUrl.trim() : "";
    const hasVideoUrl = videoUrl !== "";

    const youtubeId = (() => {
        if (!hasVideoUrl) return "";
        const m = videoUrl.match(
            /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
        );
        return m ? m[1] : "";
    })();
    const youtubeEmbedUrl = youtubeId
        ? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`
        : "";

    // Click handler for the hero "Play Event Video" button: open the inline
    // YouTube player when we can parse an id, otherwise just open the link.
    const handlePlayVideo = () => {
        if (youtubeEmbedUrl) {
            setShowVideo(true);
        } else {
            window.open(videoUrl, "_blank", "noopener,noreferrer");
        }
    };

    const eventAddress = eventDetails?.venueaddress || "";
    const renderWhenWhere = (stacked = false) => (
        <div className={`flex ${stacked ? "flex-col " : "flex-col md:flex-row  lg:gap-12"}`}>
            <div className="flex-1">
                <h3 className="text-gray-500 font-normal mb-3 md:mb-4 flex items-center text-sm md:text-base tracking-wide">
                    <div className="bg-[#ffea00] p-2 rounded-xl mr-3 shadow-sm w-[36px] h-[36px] flex items-center justify-center">
                        <Calendar className="text-black" size={20} />
                    </div>
                    When
                </h3>
                <p className="font-black text-gray-900 text-lg md:text-xl mb-0">
                    {formatDate(eventDetails.date)}
                </p>
                <p className="text-gray-500 font-bold md:text-lg mt-1">
                    {formatTimeRange()}
                </p>
            </div>
            {!stacked && <div className="hidden md:block w-px bg-gray-100"></div>}
            <div className="flex-1">
                <h3 className="text-gray-500 font-normal mb-3 md:mb-4 flex items-center text-sm md:text-base tracking-wide">
                    <div className="bg-[#ffea00] mr-3 rounded-xl shadow-sm w-[36px] h-[36px] flex items-center justify-center">
                        <MapPin className="text-black" size={20} />
                    </div>
                    Where
                </h3>
                <p className="font-black text-gray-900 mb-0 text-lg md:text-xl">
                    {eventDetails.venue || eventDetails.city}
                </p>
                <p className="text-gray-500 font-bold mb-0 md:text-lg mt-1">
                    {eventAddress}
                </p>
            </div>
        </div>
    );

    const renderPurchaseInfo = (centered = false, sticky = false) => (
        <>
            {startingPrice && (
                sticky ? (
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-500 font-bold">Starts at</span>
                        <span className="text-3xl font-black text-black">
                            From {startingPrice}
                        </span>
                    </div>
                ) : centered ? (
                    <div className="text-center mb-4">
                        <p className="text-gray-500 font-bold mb-1">Starts at</p>
                        <p className="text-3xl font-black text-black mb-0">
                            From {startingPrice}
                        </p>
                    </div>
                ) : (
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-gray-500 font-bold">Starts at</span>
                        <span className="text-3xl font-black text-black">
                          From  {startingPrice}
                        </span>
                    </div>
                )
            )}
            {hasBuyableTickets && (
                <button
                    type="button"
                    onClick={() => setOpenTicketBooking(true)}
                    className="w-full font-black py-3 border-none rounded-2xl transition-all text-lg bg-[#ffea00] hover:bg-[#e6d300] text-black shadow-lg hover:scale-[1.02]"
                >
                    Get Tickets
                </button>
            )}
            {onlineTicketingEnabled && (
            <p className="text-center text-xs text-gray-400 font-bold mt-3 flex items-center mb-0 justify-center gap-1">
                <CheckCircle size={12} className="mr-1" /> Secure checkout powered by
                <img
                    src={eventDetails?.payment_gateway === 'powertranz'
                        ? '/assets/images/powertranz.jpg'
                        : '/assets/images/stripe.jpg'}
                    alt={eventDetails?.payment_gateway === 'powertranz' ? 'PowerTranz' : 'Stripe'}
                    className="h-4 w-auto object-contain"
                />
            </p>
            )}
        </>
    );


    // The event info card. One definition, two callers:
    //   renderInfoCard(false) → hero card (top of page), unchanged.
    //   renderInfoCard(true)  → compact sticky sidebar variant.
    // Both render exactly the same required fields — When (date/time), Where
    // (venue/address), Starts-at price, Get Tickets, and the secure-checkout
    // note — so the sticky card can never grow beyond this compact set even if
    // the hero card gains content later. `compact` only trims outer padding to
    // keep the sticky height as tight as the design in Image 2. All values stay
    // dynamic (they come from the shared renderWhenWhere / renderPurchaseInfo).
    const renderInfoCard = (compact = false) => (
        <div
            className={`relative z-10 w-full bg-white rounded-[2rem] shadow-2xl border border-gray-100 ${
                compact ? "p-4" : "p-4 xl:p-5"
            }`}
        >
            {renderWhenWhere(true)}
            <hr className="border-gray-100 my-4" />
            {renderPurchaseInfo(true)}
        </div>
    );


    const renderHeroMedia = ({ compact = false } = {}) => (
        <div
            onClick={
                !compact && !hasVideoUrl && galleryImages.length > 0
                    ? () => setViewingGalleryIndex(0)
                    : undefined
            }
            onMouseEnter={
                !compact && !hasVideoUrl
                    ? (e) => {
                          const btn = e.currentTarget.querySelector("[data-view-flyer-btn]");
                          if (btn) btn.style.opacity = "1";
                      }
                    : undefined
            }
            onMouseLeave={
                !compact && !hasVideoUrl
                    ? (e) => {
                          const btn = e.currentTarget.querySelector("[data-view-flyer-btn]");
                          if (btn) btn.style.opacity = "0";
                      }
                    : undefined
            }
            className={`relative group w-full aspect-[2/1] bg-gray-100 overflow-hidden ${
                compact
                    ? ""
                    : `sm:rounded-[2rem] sm:shadow-lg sm:border border-gray-200 ${
                          !hasVideoUrl && galleryImages.length > 0 ? "cursor-pointer" : ""
                      }`
            }`}
        >
            <img
                src={heroImage}
                alt={eventDetails.name}
                className={`w-full h-full object-cover object-center ${
                    compact || hasVideoUrl
                        ? ""
                        : "transform transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                }`}
                onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = "/assets/images/event-cover-paceholder.png";
                }}
            />
            {!compact && hasVideoUrl ? (
                <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-3 z-20">
                    <button
                        type="button"
                        onClick={handlePlayVideo}
                        className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full bg-white/15 backdrop-blur-md border border-white/25 shadow-lg cursor-pointer transition-all duration-200 hover:bg-white/20 hover:scale-[1.02]"
                        aria-label="Play event video"
                    >
                        <span className="w-10 h-10 sm:w-11 sm:h-11 bg-[#ffea00] rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                            <Play size={18} className="text-black ml-0.5" fill="currentColor" />
                        </span>
                        <span className="text-white font-bold text-sm sm:text-base whitespace-nowrap">
                            Play Event Video
                        </span>
                    </button>
                    {galleryImages.length > 0 && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setViewingGalleryIndex(0);
                            }}
                            className="border-0 backdrop-blur-md bg-white px-3 py-2.5 rounded-2xl shadow-2xl text-black flex items-center gap-2 font-black text-sm transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                            aria-label="View flyers"
                        >
                            <Search size={18} /> View Flyers
                        </button>
                    )}
                </div>
            ) : (
                !compact &&
                galleryImages.length > 0 && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center z-20 pointer-events-none">
                        <button
                            type="button"
                            data-view-flyer-btn
                            className="pointer-events-auto border-0 backdrop-blur-md bg-white px-3 py-2.5 rounded-2xl shadow-2xl text-black flex items-center gap-2 font-black text-sm sm:text-sm transition-all duration-300"
                            style={{ opacity: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setViewingGalleryIndex(0);
                            }}
                        >
                            <Search size={18} /> View Flyers
                        </button>
                    </div>
                )
            )}
        </div>
    );

    const renderInfoCard1 = (compact = false) => (
        <div className="w-full bg-white rounded-[2rem] shadow-2xl border border-gray-100 overflow-hidden">
            {renderHeroMedia({ compact: true })}
            <div className={compact ? "px-4 pt-3 pb-4" : "px-4 xl:px-5 pt-3 pb-4"}>
                {renderPurchaseInfo(true, true)}
            </div>
        </div>
    );

    const renderMorphingEventCard = () => (
        <div
            ref={morphCardRef}
            className="hidden lg:block fixed left-0 top-0 z-30"
            style={{ display: "none" }}
            aria-hidden={!syncedReveal && !desktopHeroOut}
        >
            <div className="w-full h-full bg-white rounded-[2rem] shadow-2xl border border-gray-100 overflow-hidden flex flex-col">
                <div
                    className="bg-gray-100 overflow-hidden flex-shrink-0"
                    style={{
                        height: "var(--event-card-image-height, 0px)",
                    }}
                >
                    <img
                        src={heroImage}
                        alt={eventDetails.name}
                        className="w-full h-full object-cover object-center"
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null;
                            currentTarget.src = "/assets/images/event-cover-paceholder.png";
                        }}
                    />
                </div>
                <div className="flex-1 min-h-0 overflow-hidden px-4 xl:px-5 pt-3 pb-4">
                    <div
                        className="overflow-hidden"
                        style={{
                            maxHeight: "var(--event-card-details-height, 420px)",
                        }}
                    >
                        {renderWhenWhere(true)}
                        <hr className="border-gray-100 my-4" />
                    </div>
                    {renderPurchaseInfo(true, true)}
                </div>
            </div>
        </div>
    );

    // Category badge + share + wishlist trio. Rendered in the desktop back-link bar,
    // sticky bars, and below the mobile title in the hero section.
    const renderHeroActions = () => (
        <div className="flex items-center space-x-3 md:space-x-4">
            {eventDetails?.genre && (
                <span className="bg-[#ffea00] text-black font-black px-3 py-1 md:px-4 md:py-1.5 rounded-lg text-xs md:text-sm tracking-wider uppercase shadow-sm">
                    {eventDetails.genre}
                </span>
            )}
            <button
                onClick={handleShare}
                className="w-[34px] h-[34px] md:w-[40px] md:h-[40px] flex justify-center items-center md:p-2.5 bg-gray-50 hover:bg-gray-100 rounded-full text-gray-700 transition-colors shadow-sm border border-gray-100"
            >
                <Share2 className="w-[18px] h-[18px] md:w-5 md:h-5" />
            </button>
            <button className="w-[34px] h-[34px] md:w-[40px] md:h-[40px] p-2 md:p-2.5 bg-gray-50 flex justify-center items-center hover:bg-gray-100 rounded-full text-gray-700 transition-colors shadow-sm border border-gray-100">
                <Heart className="w-[18px] h-[18px] md:w-5 md:h-5" />
            </button>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-28 md:pb-20 animate-fade-in">
            {/* ============ DESKTOP BACK LINK ============ */}
            <div className="bg-white border-b border-gray-100 py-3 hidden md:block">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center border-none bg-transparent text-gray-600 hover:text-black font-bold transition-colors text-sm"
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        Back to Events
                    </button>
                    {renderHeroActions()}
                </div>
            </div>

            {/* ============ STICKY EVENT TITLE (desktop) ============
                Appears fixed below the site header once the hero has mostly
                scrolled away (desktopHeroOut). Fades/slides out when the hero
                returns. Mobile keeps its existing header-title behaviour. */}
            {eventDetails && (
                <div
                    className={`hidden lg:block fixed top-16 sm:top-20 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm transition-all duration-500 ease-out ${
                        desktopHeroOut
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 -translate-y-3 pointer-events-none"
                    }`}
                    aria-hidden={!desktopHeroOut}
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
                        <h2 className="text-xl md:text-2xl font-black text-black tracking-tight leading-tight truncate mb-0 min-w-0 flex-1">
                            {eventDetails.name}
                        </h2>
                        <div className="flex-shrink-0">
                            {renderHeroActions()}
                        </div>
                    </div>
                </div>
            )}

            {!loading && eventDetails && renderMorphingEventCard()}

            {/* ============ HERO COVER ============ */}
            <div className="w-full flex flex-col bg-white border-b border-gray-100 relative">
                {/* Mobile back chip — overlaps hero image */}
                <div className="absolute top-4 left-4 z-20 md:hidden">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 bg-white/90 backdrop-blur-md shadow-md rounded-full flex items-center justify-center text-black border-none"
                    >
                        <ArrowLeft size={20} />
                    </button>
                </div>

                <div className="max-w-7xl mx-auto w-full sm:px-6 lg:px-8 sm:pt-6 lg:pt-8">
                    <h1 className="hidden md:block text-3xl md:text-5xl lg:text-6xl font-black text-black mb-5 tracking-tight leading-tight">
                        {eventDetails?.name}
                    </h1>
                    {loading && <SkeltonLoader height="400px" />}
                    {!loading && eventDetails && (
                        <>
                            <div className="grid grid-cols-1 lg:grid-cols-[7fr_3fr] lg:gap-8 lg:items-start pb-0 md:pb-0">
                                <div ref={heroImageRef} className="min-w-0  md:mt-0">
                                    {renderHeroMedia()}
                                </div>
                                {/* Info card beside the image (desktop). This stays in
                                    layout as the FLIP source; morphCardRef is the only
                                    visible animated card. */}
                                <div
                                    ref={heroCardRef}
                                    className="hidden lg:block min-w-0"
                                    style={{ visibility: "hidden" }}
                                >
                                    {renderInfoCard(false)}
                                </div>
                            </div>
                            <div className="md:hidden px-4 pt-4 pb-2">
                                <h1
                                    ref={eventTitleRef}
                                    className="text-3xl font-black text-black mb-4 tracking-tight leading-tight"
                                >
                                    {eventDetails.name}
                                </h1>
                                {renderHeroActions()}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ============ MOBILE STICKY SUB-NAV ============ */}
            {eventDetails && (
                <div
                    ref={subNavRef}
                    className="sticky top-16 sm:top-20 z-40 bg-white border-b border-gray-200 shadow-sm md:hidden overflow-x-auto"
                    style={{ scrollbarWidth: "none" }}
                >
                    <div className="flex px-4 min-w-max">
                        <a
                            href="#about"
                            className={`px-4 py-3.5 text-sm font-black uppercase tracking-wider border-b-4 transition-colors ${
                                activeSection === "about"
                                    ? "border-[#ffea00] text-black"
                                    : "border-transparent text-gray-400 hover:text-black"
                            }`}
                        >
                            About
                        </a>
                        {eventDetails.guests?.length > 0 && (
                            <a
                                href="#headliners"
                                className={`px-4 py-3.5 text-sm font-black uppercase tracking-wider border-b-4 transition-colors ${
                                    activeSection === "headliners"
                                        ? "border-[#ffea00] text-black"
                                        : "border-transparent text-gray-400 hover:text-black"
                                }`}
                            >
                                Headliners
                            </a>
                        )}
                        {eventDetails.tickets?.length > 0 && (
                            <a
                                href="#tickets"
                                className={`px-4 py-3.5 text-sm font-black uppercase tracking-wider border-b-4 transition-colors ${
                                    activeSection === "tickets"
                                        ? "border-[#ffea00] text-black"
                                        : "border-transparent text-gray-400 hover:text-black"
                                }`}
                            >
                                Tickets
                            </a>
                        )}
                        {galleryImages.length > 1 && (
                            <a
                                href="#flyers"
                                className={`px-4 py-3.5 text-sm font-black uppercase tracking-wider border-b-4 transition-colors ${
                                    activeSection === "flyers"
                                        ? "border-[#ffea00] text-black"
                                        : "border-transparent text-gray-400 hover:text-black"
                                }`}
                            >
                                Flyers
                            </a>
                        )}
                        {eventDetails.outlets?.length > 0 && (
                            <a
                                href="#outlets"
                                className={`px-4 py-3.5 text-sm font-black uppercase tracking-wider border-b-4 transition-colors ${
                                    activeSection === "outlets"
                                        ? "border-[#ffea00] text-black"
                                        : "border-transparent text-gray-400 hover:text-black"
                                }`}
                            >
                                Ticket Outlets
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/* ============ MAIN CONTENT ============ */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10 md:pt-8 md:pb-12 lg:pt-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    <div className="space-y-12 lg:col-span-2">
                        {/* -- When / Where --
                            Mobile only: on desktop the info card (hero + sticky
                            sidebar) already carries this, so it's hidden at lg. */}
                        {eventDetails && (
                            <section className="bg-white rounded-[2rem] p-6 md:p-10 shadow-sm lg:hidden">
                                {renderWhenWhere(false)}
                            </section>
                        )}

                        {/* -- When / Where (desktop) --
                            Appears in sync with the sticky card morph. */}
                        {eventDetails && (
                            <div
                                className={`hidden lg:block overflow-hidden transition-[max-height,opacity] duration-700 ease-in-out ${
                                    syncedReveal
                                        ? "max-h-[520px] opacity-100"
                                        : "max-h-0 opacity-0 pointer-events-none"
                                }`}
                                aria-hidden={!syncedReveal}
                            >
                                <section
                                    className={`bg-white rounded-[2rem] p-6 md:p-10 shadow-sm transition-[transform,opacity] duration-700 ease-in-out ${
                                        syncedReveal
                                            ? "translate-y-0 opacity-100"
                                            : "translate-y-5 opacity-0"
                                    }`}
                                >
                                    {renderWhenWhere(false)}
                                </section>
                            </div>
                        )}

                        {/* -- About -- */}
                        {(hasDescription || hasOtherInfo) && (
                            <section
                                id="about"
                                className="bg-white rounded-[2rem] p-6 md:p-10 shadow-sm"
                                style={{ scrollMarginTop: "144px" }}
                            >
                                <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6">
                                    About event
                                </h2>
                                <div
                                    className={`text-gray-700 mb-6 text-base md:text-lg leading-relaxed font-medium space-y-4 ${
                                        !isAboutExpanded ? "max-h-72 overflow-hidden relative" : ""
                                    }`}
                                >
                                    {hasDescription && (
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: eventDetails.description,
                                            }}
                                        />
                                    )}
                                    {hasOtherInfo && (
                                        <div
                                            className="mt-3"
                                            dangerouslySetInnerHTML={{
                                                __html: eventDetails.otherinfo,
                                            }}
                                        />
                                    )}
                                    {!isAboutExpanded && (
                                        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                                    )}
                                </div>
                                <button
                                    onClick={() => setIsAboutExpanded((v) => !v)}
                                    className="bg-[#ffea00] text-black font-black px-3 py-1 md:px-4 md:py-1.5 rounded-lg text-xs md:text-sm tracking-wider uppercase shadow-sm"
                                >
                                    {isAboutExpanded ? "Show less" : "Show more"}
                                </button>
                            </section>
                        )}

                        {/* -- Headliners (Special Guests) -- */}
                        {eventDetails?.guests?.length > 0 && (
                            <section
                                id="headliners"
                                className="bg-white rounded-[2rem] p-6 md:p-10 shadow-sm border border-gray-100"
                                style={{ scrollMarginTop: "144px" }}
                            >
                                <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6">
                                    Headliners
                                </h2>
                                <div
                                    className="flex space-x-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6 md:mx-0 md:px-0 md:grid md:grid-cols-2 md:space-x-0 md:gap-4"
                                    style={{ scrollbarWidth: "none" }}
                                >
                                    {eventDetails.guests.map((guest) => (
                                        <div
                                            key={guest.id}
                                            className="flex-none w-[45vw] max-w-[180px] md:max-w-none md:w-auto snap-start relative flex flex-col md:flex-row items-center justify-center md:justify-start p-3 md:p-3 border border-gray-200 rounded-xl hover:shadow-md transition-shadow cursor-pointer bg-white text-center md:text-left"
                                        >
                                            {guest.image ? (
                                                <img
                                                    src={guest.image}
                                                    alt={guest.name}
                                                    className="w-16 h-16 md:w-14 md:h-14 rounded-full object-cover md:mr-3 border border-gray-100 flex-shrink-0"
                                                    onError={({ currentTarget }) => {
                                                        currentTarget.onerror = null;
                                                        currentTarget.style.display = "none";
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-16 h-16 md:w-14 md:h-14 rounded-full bg-gray-100  md:mb-0 md:mr-3 flex items-center justify-center text-gray-400 font-black text-xl flex-shrink-0">
                                                    {(guest.name || "?").charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <div className="flex-1 flex flex-col justify-center mt-2 md:mt-0 items-center md:items-start">
                                                <h4
                                                    className="text-base md:text-lg font-bold text-gray-900 mb-0"
                                                    style={{ textTransform: "capitalize" }}
                                                >
                                                    {guest.name}
                                                </h4>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* -- Tickets -- */}
                        {eventDetails?.tickets?.length > 0 && (
                            <section
                                id="tickets"
                                className="bg-white rounded-[2rem] p-6 md:p-10 shadow-sm "
                                style={{ scrollMarginTop: "144px" }}
                            >
                                <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-1">
                                    Tickets
                                </h2>
                                <p className="text-gray-500 font-medium mb-6">
                                    Select the ticket type you want to purchase.
                                </p>
                                <div className="flex flex-col space-y-4">
                                    {eventDetails.tickets.map((t) => {
                                        const currencyStr = t.currency || "$";
                                        // Sold out when the backend flags it OR a
                                        // limited tier has no inventory left. The
                                        // quantity_left check matters because the
                                        // flag isn't always set the moment stock
                                        // hits 0; unlimited tiers never sell out.
                                        const isSoldOut = t.is_sold_out
                                            || (!t.is_unlimited && Number(t.quantity_left ?? 0) <= 0);
                                        // Sale-window state derived from the ISO
                                        // timestamps on the ticket. The sale counts
                                        // as ended when its own window closed OR the
                                        // event is already over; an ended sale never
                                        // also reads as "not started" (no Coming Soon
                                        // / Sales start for ended or past events).
                                        const now = new Date();
                                        const saleStarts = t.sale_starts ? new Date(t.sale_starts) : null;
                                        const saleEnds = t.sale_ends ? new Date(t.sale_ends) : null;
                                        const saleWindowEnded = saleEnds && !Number.isNaN(saleEnds.getTime()) && now > saleEnds;
                                        const saleEnded = saleWindowEnded || eventEnded;
                                        const saleNotStarted = saleStarts && !Number.isNaN(saleStarts.getTime()) && now < saleStarts && !saleEnded;
                                        const canBuy = !isSoldOut && !saleNotStarted && !saleEnded;

                                        // Per-ticket buyer fee hint (service +
                                        // payment fee, per ticket) — only meaningful
                                        // for an on-sale ticket the buyer can act on.
                                        const feePerTicket = buyerFeeForItem(
                                            {
                                                fee: t.price,
                                                pass_payment_fee_to_buyer: t.pass_payment_fee_to_buyer,
                                                pass_service_fee_to_buyer: t.pass_service_fee_to_buyer,
                                            },
                                            ticketFeeConfig
                                        );

                                        // Status badge + button label/style for the
                                        // four states (sold out / not started / ended
                                        // / on sale).
                                        let badge;
                                        let buttonLabel;
                                        if (isSoldOut) {
                                            badge = { text: "Sold Out", classes: "bg-red-50 text-red-600", icon: <XCircle size={13} /> };
                                            buttonLabel = "Sold Out";
                                        } else if (saleNotStarted) {
                                            badge = { text: "Not On Sale Yet", classes: "bg-blue-50 text-blue-600", icon: <Clock size={13} /> };
                                            buttonLabel = "Coming Soon";
                                        } else if (saleEnded) {
                                            badge = { text: "Sales Ended", classes: "bg-red-50 text-red-600", icon: <XCircle size={13} /> };
                                            buttonLabel = "Sales Ended";
                                        } else {
                                            badge = {
                                                text: "On Sale Now",
                                                classes: "bg-green-50 text-green-700",
                                                icon: <span className="w-2 h-2 rounded-full bg-green-500" aria-hidden="true" />,
                                            };
                                            buttonLabel = "Buy";
                                        }

                                        return (
                                            <div
                                                key={t.id}
                                                className={`relative overflow-hidden p-4 sm:p-5 border rounded-2xl bg-white transition-all hover:shadow-sm ${
                                                    canBuy ? "border-gray-200 hover:border-[#ffea00]" : "border-gray-200"
                                                }`}
                                            >
                                                {/* Green left accent marks the currently buyable tier. */}
                                                {canBuy && (
                                                    <span className="absolute left-0 top-0 h-full w-1.5 bg-green-500" aria-hidden="true" />
                                                )}

                                                <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start sm:gap-5">
                                                    {/* ---- Left: status, name, description, meta ---- */}
                                                    <div className="min-w-0 flex-1">
                                                        <span className={`inline-flex max-w-full flex-wrap items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] sm:text-[11px] font-black uppercase tracking-wide ${badge.classes}`}>
                                                            {badge.icon}
                                                            {badge.text}
                                                        </span>

                                                        <h3 className="font-black text-gray-900 text-lg sm:text-xl mt-2 mb-0 break-words">
                                                            {t.name}
                                                        </h3>
                                                        {t.description && (
                                                            <p className="text-sm text-gray-500 font-medium mt-1 mb-0 break-words">
                                                                {t.description}
                                                            </p>
                                                        )}

                                                        {/* Sales-start callout while the tier hasn't opened yet. */}
                                                        {saleNotStarted && (
                                                            <div className="mt-3 flex w-full max-w-full flex-wrap items-start gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-gray-600 sm:w-auto">
                                                                <Calendar size={14} className="text-blue-500 shrink-0 mt-0.5" />
                                                                <span className="min-w-0 break-words">
                                                                    Sales start:{" "}
                                                                    <span className="text-blue-600">{formatSaleDateTime(t.sale_starts)}</span>
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Sales-ended callout once the tier's own sale window has
                                                            closed (only when there's a real end date to show). */}
                                                        {saleWindowEnded && (
                                                            <div className="mt-3 flex w-full max-w-full flex-wrap items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-gray-600 sm:w-auto">
                                                                <Calendar size={14} className="text-red-500 shrink-0 mt-0.5" />
                                                                <span className="min-w-0 break-words">
                                                                    Sales ended:{" "}
                                                                    <span className="text-red-600">{formatSaleDateTime(t.sale_ends)}</span>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* ---- Right: price + action ---- */}
                                                    <div className="flex w-full items-center justify-between gap-3 border-t border-gray-100 pt-4 sm:w-auto sm:shrink-0 sm:border-0 sm:pt-0 sm:gap-6">
                                                        <div className="min-w-0 text-left sm:text-right">
                                                            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">
                                                                Price
                                                            </p>
                                                            <p className="font-black text-lg sm:text-xl text-gray-900 mb-0">
                                                                {currencyStr} {amountFormatter(t.price)}
                                                            </p>
                                                            {canBuy && feePerTicket > 0 && (
                                                                <p className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 mt-0.5 mb-0">
                                                                    + {currencyStr} {amountFormatter(feePerTicket)} fee
                                                                    <Info size={12} className="text-gray-400 shrink-0" />
                                                                </p>
                                                            )}
                                                        </div>
                                                        {onlineTicketingEnabled && (
                                                            <button
                                                                onClick={() => canBuy && setOpenTicketBooking(true)}
                                                                disabled={!canBuy}
                                                                className={`border-none font-black px-4 sm:px-7 py-2.5 rounded-xl shadow-sm transition-transform text-sm whitespace-nowrap ${
                                                                    canBuy
                                                                        ? "bg-black hover:bg-gray-800 text-[#ffea00] active:scale-95"
                                                                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                }`}
                                                            >
                                                                {buttonLabel}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        )}

                        {/* -- Event Flyers gallery -- */}
                        {galleryImages.length > 1 && (
                            <section
                                id="flyers"
                                className="bg-white rounded-[2rem] p-6 md:p-10 shadow-sm overflow-hidden"
                                style={{ scrollMarginTop: "144px" }}
                            >
                                <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6">
                                    Event Flyers
                                </h2>
                                <div
                                    className="flex space-x-4 overflow-x-auto pb-4 snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0"
                                    style={{ scrollbarWidth: "none" }}
                                >
                                    {/* Skip the cover at index 0; the section header is "Event Flyers". */}
                                    {galleryImages.slice(1).map((img, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => setViewingGalleryIndex(idx + 1)}
                                            onMouseEnter={(e) => {
                                                const btn = e.currentTarget.querySelector('[data-view-flyer-btn]');
                                                if (btn) btn.style.opacity = '1';
                                            }}
                                            onMouseLeave={(e) => {
                                                const btn = e.currentTarget.querySelector('[data-view-flyer-btn]');
                                                if (btn) btn.style.opacity = '0';
                                            }}
                                            className="relative group flex-none w-[65vw] max-w-[280px] md:w-72 aspect-[4/5] snap-start cursor-pointer rounded-2xl overflow-hidden"
                                        >
                                            <img
                                                src={img}
                                                alt={`Event flyer ${idx + 1}`}
                                                className="w-full h-full object-cover object-center border border-gray-100 shadow-sm"
                                                onError={({ currentTarget }) => {
                                                    currentTarget.onerror = null;
                                                    currentTarget.style.display = "none";
                                                }}
                                            />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center z-20 pointer-events-none">
                                                <button
                                                    type="button"
                                                    data-view-flyer-btn
                                                    className="pointer-events-auto bg-white px-5 py-2.5 rounded-full shadow-2xl text-black flex items-center gap-2 font-black text-sm transition-all duration-300"
                                                    style={{ opacity: 0 }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setViewingGalleryIndex(idx + 1);
                                                    }}
                                                >
                                                    <Search size={16} /> View Flyers
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* -- Location -- */}
                        {eventDetails && (
                            <section className="bg-white rounded-[2rem] p-6 md:p-10 shadow-sm">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl md:text-3xl font-black text-gray-900">
                                        Location
                                    </h2>
                                    {eventDetails.latitude && eventDetails.longitude && (
                                        <a
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${eventDetails.latitude},${eventDetails.longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-[#ffea00] hover:bg-[#e6d300] text-black font-bold py-2 px-4 md:px-4 rounded-xl transition-colors text-sm shadow-sm inline-block"
                                        >
                                            Get Directions
                                        </a>
                                    )}
                                </div>
                                <p className="text-gray-600 font-bold mb-6 flex items-start">
                                    <MapPin className="text-black mr-2 mt-0.5 flex-shrink-0" size={20} />
                                    <span className="text-[16px]">{eventAddress}</span>
                                </p>
                                <div className="w-full h-[250px] md:h-[350px] bg-gray-200 rounded-2xl overflow-hidden relative shadow-inner border border-gray-200">
                                    <GoogleMap event={eventDetails} />
                                </div>
                            </section>
                        )}

                        {/* -- Refund Policy -- */}
                        {(eventDetails?.refund_policy || eventDetails?.refund_days != null) && (
                            <section className="bg-gray-100 rounded-[2rem] p-6 md:p-10 border border-gray-200">
                                <h2 className="text-xl md:text-2xl font-black text-gray-900 mb-3">
                                    Refund Policy
                                </h2>
                                <p className="text-gray-600 font-medium">
                                    {eventDetails.refund_policy ||
                                        "All ticket sales are final. No refunds will be issued unless the event is cancelled."}
                                </p>
                            </section>
                        )}

                        {/* -- Ticket Outlets --
                            TODO(backend): expose `outlets` collection on the
                            EventResource (likely via a join to event_handlers
                            or a new EventOutletController). Today the API does
                            not return this so the section stays hidden. */}
                        {eventDetails?.outlets?.length > 0 && (
                            <section
                                id="outlets"
                                className="bg-white rounded-[2rem] p-6 md:p-10 shadow-sm border border-gray-100"
                                style={{ scrollMarginTop: "144px" }}
                            >
                                <h2 className="text-xl md:text-2xl font-black text-gray-900 mb-2">
                                    Ticket Outlets
                                </h2>
                                <p className="text-gray-500 font-bold mb-6 text-sm ">
                                    {eventDetails.outlets.length} Outlets Available
                                </p>
                                <div className="flex flex-col space-y-4">
                                    {eventDetails.outlets.map((outlet, idx) => (
                                        <div
                                            key={idx}
                                            className="flex min-w-0 cursor-pointer items-center space-x-5 rounded-xl border border-gray-200 bg-white px-3 py-2 transition-all hover:border-[#ffea00] hover:bg-[#ffea00]/5"
                                        >
                                            <div className="w-16 h-16 rounded-xl border border-gray-200 overflow-hidden flex-shrink-0 bg-white flex items-center justify-center p-1 shadow-sm">
                                                {outlet.logo ? (
                                                    <img
                                                        src={outlet.logo}
                                                        alt={outlet.name}
                                                        className="w-full h-full object-contain rounded-lg"
                                                    />
                                                ) : (
                                                    <Store className="text-gray-400" size={24} />
                                                )}
                                            </div>
                                            <div className="flex min-w-0 flex-1 flex-col justify-center">
                                                <h4 className="mb-0 break-words font-black text-base text-gray-900">
                                                    {outlet.name}
                                                </h4>
                                                {outlet.address && (
                                                    <p className="mb-0 mt-0.5 break-words text-sm leading-snug text-gray-500">
                                                        {outlet.address}
                                                    </p>
                                                )}
                                                {/* {outlet.email && (
                                                    <p className="mb-0 break-all text-sm leading-snug text-gray-500">
                                                        {outlet.email}
                                                    </p>
                                                )} */}
                                                {outlet.parish && (
                                                    <p className="mt-2 break-words text-xs font-black text-gray-900">
                                                        {outlet.parish}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* -- Organizer / Host --
                            Pixel-matches the design screenshot:
                              - Yellow avatar with first letter of the host
                                name, soft ring + drop shadow
                              - Bold host name with blue verified badge
                                (rendered only when organizer.verified is true)
                              - "{N} Followers" line under the name (hidden
                                when no follower system exists yet)
                              - Black pill "Follow Host" CTA, always shown
                              - Hairline divider + bio paragraph below
                                (hidden when bio is empty)

                            TODO(backend): the API does not yet expose
                            `followers` or `bio`. When those land on
                            EventResource.organizer the two gated rows below
                            will start rendering automatically. */}
                        {eventDetails?.organizer?.name && (
                            <section className="bg-white rounded-[2rem] p-6 md:p-8 shadow-sm border border-gray-100">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-5">
                                    {/* Avatar + name link through to the organizer's
                                        public profile (/organizer/:slug). Only the identity
                                        block is a link — the Follow button and social
                                        icons beside it keep their own actions. */}
                                    <Link
                                        to={`/organizer/${eventDetails.organizer.profile_path}`}
                                        className="flex items-center space-x-4 no-underline rounded-2xl -m-1 p-1 transition-colors hover:bg-gray-50"
                                    >
                                        <div className="w-16 h-16 bg-[#ffea00] rounded-full flex items-center justify-center text-black font-black text-2xl border-2 border-white flex-shrink-0 shadow-sm drop-shadow-sm">
                                            {eventDetails.organizer.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            {/* whitespace-nowrap keeps the name + verified
                                                checkmark glued to a single line, regardless of
                                                container width. The parent has min-w-0 so this
                                                column can shrink in the flex layout without
                                                forcing the button to wrap. */}
                                            <h3 className="font-black text-xl text-gray-900 flex items-center leading-tight">
                                                <span className="font-black text-xl text-gray-900 flex items-center leading-tight" style={{ textTransform: "capitalize" }}>
                                                    {eventDetails.organizer.name}
                                                </span>
                                                {eventDetails.organizer.verified && (
                                                    // Twitter-style verified
                                                    // checkmark. Color matches
                                                    // X/Twitter's standard blue.
                                                    <svg
                                                        className="w-5 h-5 ml-1.5 flex-shrink-0"
                                                        style={{ color: "#1d9bf0" }}
                                                        fill="currentColor"
                                                        viewBox="0 0 24 24"
                                                        aria-label="Verified"
                                                    >
                                                        <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.918-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.337 2.25c-.416-.165-.866-.25-1.336-.25-2.21 0-3.918 1.79-3.918 4 0 .495.084.965.238 1.4-1.273.65-2.148 2.02-2.148 3.6 0 1.46.756 2.76 1.88 3.45-.044.17-.07.35-.07.53 0 2.21 1.71 4 3.918 4 .47 0 .92-.086 1.336-.25.52 1.334 1.82 2.25 3.337 2.25s2.816-.916 3.337-2.25c.416.164.866.25 1.336.25 2.21 0 3.918-1.79 3.918-4 0-.18-.026-.36-.07-.53 1.124-.69 1.88-1.99 1.88-3.45zm-12.08 4.29l-3.23-3.23 1.41-1.41 1.82 1.82 5.07-5.07 1.41 1.42-6.48 6.47z" />
                                                    </svg>
                                                )}
                                            </h3>
                                            {/* Always render the followers row when there's
                                                at least one follower OR the viewer just hit
                                                Follow (optimistic count == 1). Single-line
                                                via whitespace-nowrap matches the design. */}
                                            {followersCount > 0 && (
                                                <p className="text-gray-500 font-bold text-sm mt-0.5 whitespace-nowrap">
                                                    {formatCount(followersCount)} {followersCount === 1 ? "Follower" : "Followers"}
                                                </p>
                                            )}
                                        </div>
                                    </Link>
                                    <div className="flex items-center gap-2.5 flex-shrink-0 w-full sm:w-auto justify-start sm:justify-center">
                                        {ORGANIZER_SOCIAL_ICONS.map(({ key, label, field, altField, Icon, hoverColor, baseUrl }) => {
                                            const href = getOrganizerSocialHref(eventDetails.organizer, field, altField, baseUrl);
                                            // Hide the icon entirely when the host hasn't set this
                                            // link — only render a clickable icon for links that exist.
                                            if (!href) return null;

                                            const iconClassName = "w-9 h-9 rounded-full bg-gray-50 text-gray-500 flex items-center justify-center transition-all duration-200 hover:scale-105 flex-shrink-0";

                                            return (
                                                <a
                                                    key={key}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`${label} profile`}
                                                    className={`${iconClassName} hover:bg-white hover:shadow-sm hover:opacity-100 opacity-90`}
                                                    onMouseEnter={(e) => { e.currentTarget.style.color = hoverColor; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.color = "#6b7280"; }}
                                                >
                                                    <Icon size={16} aria-hidden="true" />
                                                </a>
                                            );
                                        })}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleToggleFollow}
                                        disabled={isFollowPending}
                                        aria-pressed={isFollowing}
                                        className={`font-black px-6 py-[13px] flex items-center jusftify-center border-none rounded-full transition-colors text-sm w-full sm:w-auto shadow-sm whitespace-nowrap disabled:opacity-60 ${
                                            isFollowing
                                                ? "bg-white border-2 border-black text-black hover:bg-gray-100"
                                                : "bg-black hover:bg-gray-800 text-[#ffea00]"
                                        }`}
                                        style={{justifyContent:"center"}}
                                    >
                                        {isFollowing ? "Following" : "Follow Host"}
                                    </button>
                                </div>
                                {eventDetails.organizer.bio && (
                                    <>
                                        <hr className="border-gray-100 my-6" />
                                        <p className="text-gray-600 font-medium text-base leading-relaxed">
                                            {eventDetails.organizer.bio}
                                        </p>
                                    </>
                                )}
                            </section>
                        )}
                    </div>

                    {/* ============ DESKTOP STICKY SIDEBAR ============
                        Compact target layout for the FLIP morph. It stays
                        mounted from the start and acts as the destination rect. */}
                    <div className="hidden lg:block lg:col-span-1">
                        {!loading && eventDetails && (
                            <div
                                ref={stickyCardRef}
                                className="sticky top-[calc(5rem+3.25rem+2rem)]"
                                style={{ visibility: "hidden" }}
                                aria-hidden={!syncedReveal}
                            >
                                {renderInfoCard1(true)}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ============ RECOMMENDED EVENTS ============
                Only renders when the /event/{id}/related endpoint returns
                at least one row — otherwise the entire block (heading +
                grid) is hidden so we don't show an empty section. */}
            {relatedEvents.length > 0 && (
                <div className="bg-white py-16 border-t border-gray-200 mt-4 md:mt-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8 md:mb-10">
                            Recommended Events
                        </h2>
                        <div
                            className="flex space-x-4 overflow-x-auto snap-x snap-mandatory pb-6 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-3 md:space-x-0 md:gap-6"
                            style={{ scrollbarWidth: "none" }}
                        >
                            {relatedEvents.slice(0, 6).map((recEvent) => {
                                // Picture source priority: horizontal_flyer
                                // → cover_image → placeholder. The related
                                // endpoint returns whatever the resource
                                // gave it; missing fields fall through.
                                const img =
                                    recEvent.horizontal_flyer ||
                                    recEvent.cover_image ||
                                    "/assets/images/event-cover-paceholder.png";
                                // Build a LOCAL date from the Y-M-D parts so a
                                // plain "2026-06-18" isn't shifted a day earlier
                                // by UTC parsing in negative-offset timezones.
                                const dateLabel = (() => {
                                    if (!recEvent.date) return "";
                                    const [y, m, d] = String(recEvent.date).split("T")[0].split("-").map(Number);
                                    const local = (y && m && d) ? new Date(y, m - 1, d) : new Date(recEvent.date);
                                    return isNaN(local.getTime())
                                        ? ""
                                        : local.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                                })();
                                return (
                                    <Link
                                        key={recEvent.id}
                                        to={`/events/${recEvent.slug}`}
                                        className="flex-none w-[75vw] max-w-[280px] md:max-w-none md:w-auto snap-start group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col cursor-pointer border border-gray-100 no-underline"
                                    >
                                        <div className="relative aspect-[16/9] md:aspect-[2/1] w-full overflow-hidden bg-gray-100">
                                            <img
                                                src={img}
                                                alt={recEvent.name}
                                                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                                onError={({ currentTarget }) => {
                                                    currentTarget.onerror = null;
                                                    currentTarget.src =
                                                        "/assets/images/event-cover-paceholder.png";
                                                }}
                                            />
                                            {recEvent.genre && (
                                                <div className="absolute top-3 left-3 bg-[#ffea00] text-black text-[10px] font-black px-2 py-1 rounded uppercase shadow-sm">
                                                    {recEvent.genre}
                                                </div>
                                            )}
                                        </div>
                                        <div className="px-4 py-3 flex flex-col flex-grow">
                                            <h3 className="font-black text-gray-900 text-[16px] line-clamp-1 mb-2 group-hover:underline decoration-[#ffea00] decoration-2">
                                                {recEvent.name}
                                            </h3>
                                            <div className="flex items-center justify-between text-xs font-bold text-gray-500 mt-auto gap-2">
                                                {dateLabel && (
                                                    <span className="flex items-center text-xs">
                                                        <Calendar
                                                            size={12}
                                                            className="mr-1 text-black"
                                                        />
                                                        {dateLabel}
                                                    </span>
                                                )}
                                                {(recEvent.venue || recEvent.city) && (
                                                    <span className="flex items-center truncate">
                                                        <MapPin
                                                            size={12}
                                                            className="mr-1 text-black flex-shrink-0"
                                                        />
                                                        <span className="truncate text-xs">
                                                            {recEvent.venue || recEvent.city}
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* ============ MOBILE FIXED CTA BAR ============
                Hidden entirely when nothing is buyable so the page
                doesn't carry a non-functional sold-out bar at the
                bottom. Matches the desktop sidebar gating. */}
            {hasBuyableTickets && (
                <div className="fixed bottom-0 left-0 w-full z-50 lg:hidden bg-[#ffea00] border-t border-yellow-500 shadow-[0_-10px_30px_rgba(0,0,0,0.15)]">
                    <button
                        type="button"
                        onClick={() => setOpenTicketBooking(true)}
                        className="w-full font-black py-4 text-xl flex border-none items-center justify-center transition-colors text-black bg-[#e6d300]"
                    >
                        Tickets <ArrowRight className="ml-2" size={24} />
                    </button>
                </div>
            )}

            {/* ============ MODALS ============ */}
            <FlyerGallery
                open={viewingGalleryIndex !== null}
                onClose={() => setViewingGalleryIndex(null)}
                images={galleryImages}
                initialIndex={viewingGalleryIndex || 0}
            />

            {/* Inline YouTube player for the promotional event video. */}
            {showVideo && youtubeEmbedUrl && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4 animate-fade-in"
                    onClick={() => setShowVideo(false)}
                >
                    <button
                        type="button"
                        onClick={() => setShowVideo(false)}
                        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white flex items-center justify-center transition-colors"
                        aria-label="Close video"
                    >
                        <X size={22} />
                    </button>
                    <div
                        className="w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <iframe
                            src={youtubeEmbedUrl}
                            title="Event video"
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </div>
            )}

            {/* New two-step checkout: Select Tickets → (hand off to) Payment.
                Replaces the legacy TicketBooking2 + RegisterTickets chain. */}
            <TicketSelectModal
                open={onlineTicketingEnabled && openTicketBooking}
                onClose={() => setOpenTicketBooking(false)}
                onContinue={handleContinue}
            />
            <Payment
                openPayment={openPayment}
                handleClose={() => setOpenPayment(false)}
            />

            <SocialShare
                open={openSocialShare}
                handleClose={() => setOpenSocialShare(false)}
                event={eventDetails}
            />
        </div>
    );
}
